import Add from "@mui/icons-material/Add";
import Check from "@mui/icons-material/Check";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { stateRequiresWasteStreamRegistration } from "encamp-shared/src/waste/constants";
import { gql } from "generated-graphql";
import {
  WasteClassification,
  WasteStreamRegistrationStatus,
  WasteStreamsQuery,
  WasteStreamsQueryVariables,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useOmnisearchInput } from "hooks/useOmnisearchDatagrid/useOmnisearchInput";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { useShowWasteStateRegistration } from "hooks/useWasteStateRegistration";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreateWasteStreamRegistrationSubmissionsDialog,
  RegistrationStream,
} from "routes/Customer/Waste/WasteStreams/CreateWasteStreamRegistrationSubmissionsDialog";

import { WasteStreamAmountCell, WasteStreamAmountHeader } from "../../../WasteStreamAmountCell";
import { profileNumberValueGetter } from "../formatWasteStreamProfileNumber";
import { MergedWasteStreamChip } from "../MergedWasteStreamChip";
import { prettyPrintWasteClassification } from "../prettyPrintWasteClassification";
import { prettyPrintWasteStreamRegistrationStatus } from "../prettyPrintWasteStreamRegistrationStatus";
import { ProvisionalWasteStreamChip } from "../ProvisionalWasteStreamChip";
import { useWasteFacility } from "../useWasteFacility";
import { useWasteFacilityState } from "../WasteFacilityContext";
import { WasteStreamDetailsModal } from "../WasteStreamDetails/WasteStreamDetailsModal";
import { useWasteStreamSelection } from "./useWasteStreamSelection";
import { WasteStreamExportDropdown } from "./WasteStreamExportDropdown";
import { WasteStreamMergeDialog } from "./WasteStreamMerge/WasteStreamMergeDialog";

const WASTE_STREAMS = gql(`
  query WasteStreams(
    $facilityId: ID!,
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
    $startDate: Date
    $endDate: Date
    $view: FacilityWasteView
    $tags: [String!]
    $needsStateRegistration: Boolean
  ) {
    facility(id: $facilityId) {
      state
      wasteStreams(search: $search, page: $page, pageSize: $pageSize, sort: $sort, startDate: $startDate, endDate: $endDate, view: $view, tags: $tags, needsStateRegistration: $needsStateRegistration) {
        items {
          id
          name
          facilityId
          classification
          federalWasteCodes
          stateWasteCodes
          eManifestProfileNumber
          vendorProfileNumber
          formCode
          sourceCode
          managementMethodCodes
          vendorAmount
          eManifestAmount
          documentAmount
          maxAmount
          vendorAmountAllTime
          eManifestAmountAllTime
          mergeCount
          comment
          willNotReport
          stateRegisteredId
          stateRegistrationStatus
          stateRegisteredAt
          isProvisional
          openRegistrationSubmission {
            id
          }
        }
        count
      }
    }
  }
`);

type WasteStreamTileRow = NonNullable<
  NonNullable<WasteStreamsQuery["facility"]>["wasteStreams"]
>["items"][number];

// Give each column a fixed initial width derived from its old `flex` weight so
// the grid overflows and scrolls horizontally instead of squishing columns to
// fit. minWidth stays low (the MUI default) so columns remain resizable to well
// under the initial width.
const COLUMN_WIDTH_PER_FLEX = 280;
const MIN_RESIZE_WIDTH = 50;

// The shared DataGrid shows a pointer cursor on row hover; with row click
// disabled in readOnly mode that would falsely signal clickability, so put the
// default cursor back. Module-level constant so `gridSx` identity is stable.
const READ_ONLY_GRID_SX = {
  "& .MuiDataGrid-row:hover": { cursor: "default" },
};

function rowNeedsRegistration(row: WasteStreamTileRow, facilityState: string | null): boolean {
  return (
    !row.stateRegisteredId &&
    !row.willNotReport &&
    stateRequiresWasteStreamRegistration(facilityState)
  );
}

interface WasteStreamsDataGridProps {
  facilityId: string;
  /**
   * Strips every edit affordance — row-click details modal (and its staff edit
   * dialog), staff merge selection/button, and the registration-submission cell
   * actions — leaving a review-only grid. Search, sort, filters, and export
   * remain. Used by the waste submission stepper, where edits must happen on the
   * waste facility page instead of inline.
   */
  readOnly?: boolean;
}

export const WasteStreamsDataGrid = ({ facilityId, readOnly }: WasteStreamsDataGridProps) => {
  const theme = useTheme();
  const { isStaff } = useCurrentUser();
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const { data } = useWasteFacility(facilityId);
  const { selectedRows, setSelectedRows, isRowSelectable, canMergeSelected } =
    useWasteStreamSelection<WasteStreamTileRow>();

  const { selectedId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("wasteStreamId", {
    prefixSegment: "wasteStream",
  });

  const handleRowClick = (params: GridRowParams<WasteStreamTileRow>) => {
    pushIdToUrl(params.row.id);
  };

  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const { omnisearch } = useOmnisearchInput({ isURLDriven: true });
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [, setQueryData] = useState<WasteStreamsQuery | undefined>(undefined);
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [registrationStreams, setRegistrationStreams] = useState<RegistrationStream[]>([]);

  const facilityState = data?.facility?.state ?? null;
  const showRegistrationColumn = useShowWasteStateRegistration(facilityState);

  const openRegistrationDialog = useCallback((streams: RegistrationStream[]) => {
    setRegistrationStreams(streams);
    setRegistrationDialogOpen(true);
  }, []);

  const exportFilename = useMemo(
    () => (data.facility?.name ? `${data.facility.name}-waste-streams` : undefined),
    [data.facility?.name]
  );

  const columns: OmnisearchGridColDef<WasteStreamTileRow>[] = useMemo(() => {
    const base: OmnisearchGridColDef<WasteStreamTileRow>[] = [
      {
        field: "name",
        headerName: "Waste Stream",
        flex: 1,
        minWidth: 100,
        sortable: true,
        headerClassName: "wrapHeader",
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  title={params.value}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.value}
                </Typography>
                {params.row.willNotReport && (
                  <Typography variant="caption" color="text.secondary">
                    Not Reportable
                  </Typography>
                )}
              </Box>
              {isStaff && <MergedWasteStreamChip mergeCount={params.row.mergeCount} />}
              <ProvisionalWasteStreamChip isProvisional={params.row.isProvisional} />
            </Box>
            {!readOnly && <ArrowRightIcon sx={{ flexShrink: 0, fontSize: 16 }} />}
          </Box>
        ),
      },
      {
        field: "profileNumber",
        headerName: "Profile Number",
        flex: 0.5,
        sortable: true,
        valueGetter: profileNumberValueGetter,
      },
      {
        field: "federalWasteCodes",
        headerName: "Federal Waste Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          const federalWasteCodes = row.federalWasteCodes || [];
          return federalWasteCodes.join(", ");
        },
        headerClassName: "wrapHeader",
      },
      {
        field: "stateWasteCodes",
        headerName: "State Waste Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          const stateWasteCodes = row.stateWasteCodes || [];
          return stateWasteCodes.join(", ");
        },
        headerClassName: "wrapHeader",
      },
      {
        field: "classification",
        headerName: "Classification",
        flex: 0.5,
        filterable: false,
        sortable: true,
        valueFormatter: (_, row) => prettyPrintWasteClassification(row.classification),
        headerClassName: "wrapHeader",
      },
      {
        field: "formCode",
        headerName: "Form Code",
        flex: 0.3,
        sortable: true,
        headerClassName: "wrapHeader",
      },
      {
        field: "sourceCode",
        headerName: "Source Code",
        flex: 0.3,
        sortable: true,
        headerClassName: "wrapHeader",
      },
      {
        field: "managementMethodCodes",
        headerName: "Management Method Code",
        flex: 0.5,
        sortable: true,
        valueGetter(_, row) {
          return row.managementMethodCodes.join(", ");
        },
        headerClassName: "wrapHeader",
      },
      {
        // Max reported amount across Vendor / e-Manifest / Document, with a
        // per-source tooltip. Not filterable: maxAmount is a server aggregate.
        field: "maxAmount",
        headerName: "Amount",
        flex: 0.4,
        filterable: false,
        sortable: true,
        headerClassName: "wrapHeader",
        renderHeader: () => <WasteStreamAmountHeader />,
        renderCell: (params) => (
          <WasteStreamAmountCell
            vendorAmount={params.row.vendorAmount}
            eManifestAmount={params.row.eManifestAmount}
            documentAmount={params.row.documentAmount}
          />
        ),
      },
      {
        field: "willNotReport",
        headerName: "Not Reportable",
        filterKeyType: "boolean",
        sortable: false,
        flex: 0.4,
        headerClassName: "wrapHeader",
        renderCell: ({ value }) => (value === true ? <Check /> : null),
      },
    ];
    if (showRegistrationColumn) {
      base.splice(
        2,
        0,
        {
          field: "stateRegisteredId",
          headerName: "State Registered ID",
          sortable: true,
          filterable: true,
          flex: 0.6,
          headerClassName: "wrapHeader",
          // When the stream has a registered ID we show it; otherwise we offer
          // the Staff-only create/open submission action right in this cell.
          // Non-staff just see a blank cell (ENG-15415).
          renderCell: ({ value, row }) => {
            if (value) return value;
            if (!isStaff || readOnly) return null;
            const openSub = row.openRegistrationSubmission;
            if (openSub) {
              return (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<OpenInNew fontSize="small" />}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/o/${tenantId}/waste/submissions/${openSub.id}/edit`);
                  }}
                >
                  Submission
                </Button>
              );
            }
            if (!rowNeedsRegistration(row, facilityState)) return null;
            return (
              <Button
                size="small"
                variant="text"
                startIcon={<Add fontSize="small" />}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                onClick={(e) => {
                  e.stopPropagation();
                  openRegistrationDialog([
                    {
                      id: row.id,
                      name: row.name,
                      facilityName: data.facility?.name ?? "—",
                      state: facilityState ?? "",
                    },
                  ]);
                }}
              >
                Submission
              </Button>
            );
          },
        },
        {
          field: "stateRegistrationStatus",
          headerName: "Registration Status",
          sortable: true,
          filterable: false,
          flex: 0.5,
          headerClassName: "wrapHeader",
          valueFormatter: (_, row) =>
            prettyPrintWasteStreamRegistrationStatus(row.stateRegistrationStatus),
        }
      );
    }
    return base.map(({ flex, ...rest }) => ({
      ...rest,
      width: rest.width ?? Math.round((flex ?? 1) * COLUMN_WIDTH_PER_FLEX),
      minWidth: MIN_RESIZE_WIDTH,
    }));
  }, [
    isStaff,
    readOnly,
    showRegistrationColumn,
    facilityState,
    openRegistrationDialog,
    data.facility?.name,
    navigate,
    tenantId,
  ]);

  return (
    <>
      <OmnisearchDataGrid<WasteStreamsQuery, WasteStreamsQueryVariables, WasteStreamTileRow>
        persistenceKey="waste-streams-table"
        isURLDriven={true}
        columns={columns}
        dataQuery={WASTE_STREAMS}
        getItems={(data) => data.facility?.wasteStreams?.items ?? []}
        getCount={(data) => data.facility?.wasteStreams?.count ?? 0}
        dataQueryVariables={{
          facilityId,
          startDate: deferredFilters.startDate,
          endDate: deferredFilters.endDate,
          view: deferredFilters.view,
          tags: deferredFilters.tags,
        }}
        initialPageSize={10}
        withPadding={false}
        defaultSearch=""
        noDataMessage={"No Waste Streams found for this facility."}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        onRowClick={readOnly ? undefined : handleRowClick}
        gridSx={readOnly ? READ_ONLY_GRID_SX : undefined}
        disableRowSelectionOnClick={true}
        onDataStateChange={(data) => {
          setQueryData(data ?? undefined);
        }}
        commandButtons={[
          isStaff && !readOnly ? (
            <Button
              key="merge-waste-streams-button"
              size="small"
              variant="contained"
              sx={{
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.warning.light,
              }}
              endIcon={<ArrowRightIcon />}
              disabled={!canMergeSelected}
              title={`Merge selected waste streams`}
              onClick={() => setIsMergeDialogOpen(true)}
            >
              Merge Waste Streams
            </Button>
          ) : null,
          <WasteStreamExportDropdown
            key="export-waste-streams-button"
            isStaff={isStaff}
            disabled={!facilityId}
            facilityId={facilityId}
            tenantId={tenantId ?? ""}
            fileName={exportFilename}
            filter={{
              search: omnisearch,
              startDate: deferredFilters.startDate,
              endDate: deferredFilters.endDate,
              view: deferredFilters.view,
              tags: deferredFilters.tags,
            }}
            dataQuery={WASTE_STREAMS}
            dataQueryVariables={{
              facilityId,
              startDate: deferredFilters.startDate,
              endDate: deferredFilters.endDate,
              view: deferredFilters.view,
              tags: deferredFilters.tags,
              search: omnisearch || undefined,
            }}
            onWasteStreamClick={pushIdToUrl}
          />,
        ]}
        selectedIds={selectedRows.map((row) => row.id)}
        onSelectedRowsChanged={isStaff && !readOnly ? setSelectedRows : undefined}
        isRowSelectable={isRowSelectable}
        checkboxSelection={isStaff && !readOnly}
        disableSelectAll
        additionalFilterColumns={[
          {
            filterKeyType: "enumAutocomplete",
            key: "classification",
            header: "Classification",
            enumValues: Object.values(WasteClassification),
            enumPresentationFunction: (value) => prettyPrintWasteClassification(value),
          },
          ...(showRegistrationColumn
            ? [
                {
                  filterKeyType: "boolean" as const,
                  key: "needsStateRegistration",
                  header: "Needs State Registration",
                },
                {
                  filterKeyType: "enumAutocomplete" as const,
                  key: "stateRegistrationStatus",
                  header: "Registration Status",
                  enumValues: Object.values(WasteStreamRegistrationStatus),
                  ignoreStateAbbreviations: true,
                },
              ]
            : []),
        ]}
      />

      {isMergeDialogOpen && (
        <WasteStreamMergeDialog
          onClose={() => {
            setIsMergeDialogOpen(false);
            setSelectedRows([]); // Clear selections when dialog closes
          }}
          selectedRows={selectedRows}
        />
      )}

      {registrationDialogOpen && (
        <CreateWasteStreamRegistrationSubmissionsDialog
          open
          onClose={() => setRegistrationDialogOpen(false)}
          tenantId={tenantId ?? ""}
          streams={registrationStreams}
          onSuccess={() => {}}
        />
      )}

      {/* readOnly also guards deep links: `wasteStreamId` in the URL would
          otherwise open the modal (and its staff edit dialog) without a click. */}
      {!readOnly && selectedId && (
        <WasteStreamDetailsModal
          open={!!selectedId}
          onClose={() => popIdFromUrl()}
          wasteStreamId={selectedId}
          buildLineItemHref={(lineItemId) => `../manifest-line-items/${lineItemId}`}
        />
      )}
    </>
  );
};
